<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import SingleSelect from '~/components/form-elements/SingleSelect.vue'
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import russianPostCountries from '~/constants/russian-post-countries'
import OrderShippingType from '~/enums/order-shipping-type'
import helpers from '~/helpers'
import { useCartStore } from '~/store/cart'

const model = defineModel<boolean>()

const props = defineProps<{
  appliedPromoCodeName: string | null
}>()

const messages = {
  failedToCalculateShippingCost: 'Не удалось рассчитать стоимость доставки.'
}

const { $apiHelper, $toast } = useNuxtApp()
const cartStore = useCartStore()

const errorMessage = ref<string | null>(null)
const isLoading = ref(false)
const shippingCostInKopecks = ref<number | null>(null)

// Country.
const autocompleteCountries = ref<any[]>([])
const countryQuery = ref<any>(null)
const selectedCountry = ref<any>(null)

watch(selectedCountry, async (x: object | null): Promise<void> => {
  if (x) {
    await calculate()
  }
})
watch(countryQuery, (x: string): void => {
  if (!x) {
    autocompleteCountries.value = russianPostCountries

    return
  }

  autocompleteCountries.value = russianPostCountries.filter(
    (y: any): boolean => y.name.toLowerCase().includes(x.toLowerCase())
  )
})

function beforeOpen (): void {
  autocompleteCountries.value = russianPostCountries
}

function close (): void {
  model.value = false
}

function confirmSelection (): void {
  const { id: countryId, name: countryName } = selectedCountry.value

  cartStore.shippingCostInKopecks = shippingCostInKopecks.value
  cartStore.shippingData = { countryId, countryName }

  close()
}

async function calculate (): Promise<void> {
  isLoading.value = true

  errorMessage.value = null
  shippingCostInKopecks.value = null

  const { appliedPromoCodeName } = props

  const extraData: any = { countryId: selectedCountry.value.id }

  if (appliedPromoCodeName) {
    extraData.promoCodeName = appliedPromoCodeName
  }

  try {
    const { meta } = await $apiHelper.actions.getShippingCost(
      OrderShippingType.RussianPostInternationalParcel,
      cartStore.weightInGrams as number,
      extraData
    ) as any
    errorMessage.value = meta.errorMessage
    shippingCostInKopecks.value = meta.shippingCostInKopecks
  } catch (error) {
    console.error(error)

    $toast.error(messages.failedToCalculateShippingCost)
  }

  isLoading.value = false
}

function updateSelectedCountry (x: any, isAdding: boolean): void {
  selectedCountry.value = x

  if (isAdding) {
    autocompleteCountries.value = []
  }
}
</script>

<template>
  <CustomModalWindow v-model="model" @before-open="beforeOpen">
    <template #title>
      Выбор страны для международной доставки&nbsp;— EMS Почта России
    </template>

    <Notification v-if="errorMessage" class="mb-6" is-small status="error">
      {{ errorMessage }}
    </Notification>

    <Notification v-if="shippingCostInKopecks !== null" class="mb-6" is-small status="info">
      Стоимость доставки: {{ helpers.formatPrice(shippingCostInKopecks, true) }}&nbsp;₽.
    </Notification>

    <div class="mb-6 min-h-80">
      <SingleSelect
        v-model="countryQuery"
        :autocomplete-items="autocompleteCountries"
        is-required
        label-text="Страна"
        placeholder="Выберите из списка"
        :selected-item="selectedCountry"
        @update-selected-item="updateSelectedCountry"
      />
    </div>

    <BasicButton
      v-if="selectedCountry && errorMessage === null && shippingCostInKopecks !== null"
      color="green"
      :is-loading="isLoading"
      title="Подтвердить выбор"
      @click="confirmSelection"
    />

    <template #action>
      <a href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>
